// The page editor's small action menus: the header's More menu and the "..."
// menu on each rail row. An icon button opens a list rendered in a portal (so
// it is never clipped by a scrolling panel), placed under the button and
// flipped up when there is no room below. Esc closes it and returns focus to
// the button; arrow keys, Home and End move between items; a click outside
// closes it.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface EditorMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Extra classes, e.g. 'xl:hidden' for an item that only shows below 1280px. */
  className?: string;
}
export interface EditorMenuSeparator { separator: true; className?: string }
export type EditorMenuEntry = EditorMenuItem | EditorMenuSeparator;

const isSeparator = (e: EditorMenuEntry): e is EditorMenuSeparator => 'separator' in e;

export function EditorMenu({ label, items, icon, buttonClassName, title, width = 200, align = 'right' }: {
  /** The button's accessible name, also the menu's. */
  label: string;
  items: EditorMenuEntry[];
  icon: ReactNode;
  buttonClassName?: string;
  /** Tooltip on the button (none by default). */
  title?: string;
  width?: number;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    setPos(null);
    if (refocus) btnRef.current?.focus();
  }, []);

  // Place the list next to the button once it has a size: below it, or above
  // it when the space below is too short.
  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const h = menuRef.current?.offsetHeight ?? 240;
    const up = r.bottom + 6 + h > window.innerHeight - 8 && r.top - 6 - h > 8;
    const x = align === 'right' ? { right: Math.max(8, window.innerWidth - r.right) } : { left: Math.max(8, r.left) };
    // The menu grows out of the corner nearest its button.
    const transformOrigin = `${up ? 'bottom' : 'top'} ${align}`;
    setPos(up ? { bottom: window.innerHeight - r.top + 6, ...x, transformOrigin } : { top: r.bottom + 6, ...x, transformOrigin });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const visible = () => [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])]
      .filter((el) => getComputedStyle(el).display !== 'none');
    visible()[0]?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); close(true); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const list = visible();
      if (!list.length) return;
      const at = list.indexOf(document.activeElement as HTMLButtonElement);
      const next = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1
        : (at + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
      list[next].focus();
    };
    const onResize = () => close();
    // Capture: runs before the editor's own Esc handler (close a drawer, deselect).
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  function choose(it: EditorMenuItem) {
    close();
    it.onSelect();
    // Keyboard users keep their place: focus goes back to the button unless
    // the action moved it somewhere else (a dialog, a field).
    setTimeout(() => {
      const a = document.activeElement;
      if ((!a || a === document.body) && btnRef.current?.isConnected) btnRef.current.focus({ preventScroll: true });
    }, 0);
  }

  return (
    <>
      <button ref={btnRef} type="button" className={buttonClassName} aria-label={label} title={title}
        aria-haspopup="menu" aria-expanded={open} onClick={() => (open ? close() : setOpen(true))}>
        {icon}
      </button>
      {open && createPortal(
        // pe-scope: the menu lives on document.body, outside the editor root.
        <div className="pe-scope">
          {/* Catches the click outside (the canvas iframe included) and closes. */}
          <div className="fixed inset-0 z-[1074]" onClick={() => close()} aria-hidden="true" />
          <div ref={menuRef} role="menu" aria-label={label} className="pe-menu"
            style={{ ...(pos ?? { top: -9999, left: -9999 }), minWidth: width }}>
            {items.map((it, i) => isSeparator(it)
              ? <div key={`sep-${i}`} role="separator" className={`pe-menu-sep ${it.className ?? ''}`} />
              : (
                <button key={it.label} type="button" role="menuitem" disabled={it.disabled}
                  className={`pe-menu-item ${it.danger ? 'pe-menu-item--danger' : ''} ${it.className ?? ''}`}
                  onClick={() => choose(it)}>
                  {it.icon}
                  {it.label}
                </button>
              ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
